import { ITableItem, IBooking, IServiceLayoutItem, IServiceItem } from 'shared-types/index';
import DateUtilsService from 'external-services/date-utils-service';
import ServiceDetectionService from "external-services/service-detection-service/serviceDetection.service";

const NS = 'BookingDragService';

class BookingDragService {

  /**
   * `service.type` can sometimes be lower or pascal case so we handle both here.
   * Note: this also includes private functions, as they have:
   * `service.type = 'event'
   * and
   * `service.eventType = 'PrivateFunction'`
   */
  serviceIsEventType(serviceType: string): boolean {
    return serviceType === 'event' || serviceType === "Event";
  }

  /**
   * Tries to find a paid service at a specified time, or a booking option (free or paid)
   * @param services - services to check (probably from `schedule.services`)
   * @param bookingTime - time to check from a booking
   */
  bookingComesFromPaidService(services: IServiceItem[], bookingTime: Date, dragBookingOrgServiceId: string): boolean {

    /**
     * First tries to use the service based on service id, which will usually be the real-world use case
     */
    const fromService = services.find(s => s._id === dragBookingOrgServiceId);
    if (fromService && DateUtilsService.isInBetweenDates(bookingTime, fromService.startTime as Date, fromService.endTime as Date)) {
      return ServiceDetectionService.serviceIsPaidOrBookingOptions(fromService);
    }


    /**
     * If the service is invalid for some reason, then tries to find to first service in the time slot and assumes it
     * to be 'from service'
     */
    return services.some(service => {
      if (DateUtilsService.isInBetweenDates(bookingTime, service.startTime as Date, service.endTime as Date)) {
        return ServiceDetectionService.serviceIsPaidOrBookingOptions(service);
      }
      return false;
    });
  }

  /**
   * 2 funcs are moved to service-detection-service submodule
   */
  // serviceIsPaidOrBookingOptions(service: IServiceItem): boolean
  // hasSectionsInService(serviceId: string, sectionId: string, serviceLayouts: IServiceLayoutItem[]): boolean


  /**
   * Checks if booking is in a legitimate event; checking type, start & end time, plus that the sectionId on the table
   * exists and is not null.
   * @param services - services to check (probably from `schedule.services`)
   * @param bookingTime - time to check from a booking
   * @param table - table to verify
   * @param omitClosedLayouts - if true, will invalidate layouts that are closed
   */
  checkForLegitEvent(services: IServiceItem[], bookingTime: Date, table: ITableItem, omitClosedLayouts: boolean): boolean {
    return services.some(service => {
      if (this.serviceIsEventType(service.type)) {
        if (DateUtilsService.isInBetweenDates(bookingTime, service.startTime as Date, service.endTime as Date)) {
          return service.sectionLayouts.some(sectionLayout => {
            const matchesTableSection = table.sectionId === sectionLayout.sectionId;
            if (!omitClosedLayouts) { // includes closed layouts
              return matchesTableSection;
            }
            const isLayoutClosed = sectionLayout.layoutId === null;
            return matchesTableSection && !isLayoutClosed; // omits closed layouts
          });
        }
      }
      return false;
    });
  }


  /**
   * Find the correct service id for a booking and sets serviceId on it
   * @param booking - booking to target
   * @param serviceLayouts - serviceLayouts from schedule
   * @param newTable - optionally pass a different table (useful for table swap)
   */
  setServiceId(booking: IBooking, serviceLayouts: IServiceLayoutItem[], newTable?: ITableItem): void {
    if (booking) {
      const table = newTable
        ? newTable
        : (booking.tables && booking.tables.length)
          ? booking.tables[0]
          : null;

      if (!table) {
        return;
      }

      // the booking needs a table for its service id to be found
      serviceLayouts.some(serviceLayout => {
        const bookingWithinServiceLayout: boolean = DateUtilsService.isInBetweenDates(booking.time, serviceLayout.startTime as unknown as Date, serviceLayout.endTime as unknown as Date);
        const tableSectionMatchesServiceLayoutSection: boolean = serviceLayout.sectionId === table.sectionId;

        if (bookingWithinServiceLayout && tableSectionMatchesServiceLayoutSection && serviceLayout.layoutId) {
          booking.serviceId = serviceLayout.serviceId;
          return true;
        }
        return false;
      });
    }
  }

  /**
   *
   * @param service - the original service of where the booking was dragged from
   * @param sticksToOriginalService - if the selected service should 'stick' to the same service it was dragged from
   * @param hasEvents - true if the target booking falls on a section (in an event) regardless of if it is set to 'Closed'
   */
  getSelectedServiceIdForOriginalServiceDraggedFrom(
    service: IServiceItem, sticksToOriginalService: boolean, hasEvents: boolean
  ): string {

    if (sticksToOriginalService || hasEvents && this.serviceIsEventType(service.type)) {
      return service._id;
    }

    return null;
  }

  getSelectedServiceIdForServiceDraggedTo(
    service: IServiceItem, sticksToOriginalService: boolean, hasEvents: boolean, dragBookingOrgServiceId: string
  ): string {
    const isEventType = this.serviceIsEventType(service.type);

    if (sticksToOriginalService) {
      return dragBookingOrgServiceId;
    }

    // if service is not an event returns the service's id (to be selected)
    if (!hasEvents && !isEventType) {
      return service._id;
    }

    /**
     * @todo: do we need this? Is it possible for hasEvents to be false and isEventType to be true?
     */
    if (!hasEvents) { //  && !sticksToOriginalService
      return service._id;
    }

    // if dragging to another event, use the new event's service id
    if (hasEvents && isEventType) {
      return service._id;
    }

    return null;
  }

  // old version of logic @todo: test against this in unit tests
  // getSelectedServiceIdForServiceDraggedTo_old(service: IServiceItem, sticksToOriginalService: boolean, hasEvents: boolean, dragBookingOrgServiceId: string): string {
  //
  //   if ((dragBookingOrgServiceId !== service._id) &&
  //     (!hasEvents && service.type !== 'event')
  //   ) {
  //     return service._id;
  //   } else {
  //     if (sticksToOriginalService) {
  //       return dragBookingOrgServiceId;
  //     }
  //   }
  //
  //   /**
  //    * @todo: 'hasEvent' is the name of a function, but is not actually called. Determine if this is meant to be `hasEvents`.
  //    */
  //   if (dragBookingOrgServiceId !== service._id && !hasEvents && !sticksToOriginalService) {
  //     return service._id;
  //   }
  //
  //   if (sticksToOriginalService) {
  //     return dragBookingOrgServiceId;
  //   }
  //
  //   if (hasEvents && service.type === 'event') {
  //     return service._id;
  //   }
  //
  //   return null;
  // }


  setSectionOfDraggedBooking(
    dragBooking: IBooking, fromSectionId: string, newSectionId: string, swapWithBooking: IBooking
  ): void {
    dragBooking.preferredSectionId = newSectionId;
    if (swapWithBooking) {
      swapWithBooking.preferredSectionId = fromSectionId;
    }
  }

}
const instance = new BookingDragService();
export default instance as BookingDragService;
